import { __rest } from "tslib";
import { GRID_DEFAULTS } from '../constants/grid';
export function createFormView({ form, path = '', stepId, recursive = true, styleParams, includeHiddenFields = false, isMobile = false, }) {
    var _a, _b, _c, _d, _e, _f;
    const step = stepId
        ? (_a = form.steps) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === stepId)
        : (_b = form.steps) === null || _b === void 0 ? void 0 : _b[0];
    const layout = (_c = step === null || step === void 0 ? void 0 : step.layout) !== null && _c !== void 0 ? _c : {};
    const fields = (_d = form.fields) !== null && _d !== void 0 ? _d : [];
    const nameView = (_f = (_e = form.properties) === null || _e === void 0 ? void 0 : _e.name) !== null && _f !== void 0 ? _f : '';
    const fieldViews = fields
        .filter((field) => includeHiddenFields || !field.hidden)
        .filter((field) => { var _a, _b; return (_b = (_a = layout === null || layout === void 0 ? void 0 : layout.large) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.find((item) => field.id === item.fieldId); })
        .reduce((result, field) => {
        const fieldView = createFieldView(field, layout, path, recursive, styleParams, isMobile);
        if (fieldView) {
            result.push(fieldView);
        }
        return result;
    }, [])
        .sort(sortFieldViewsByColumn)
        .sort(sortFieldViewsByRow);
    const gridView = createGridView(layout, styleParams, isMobile);
    let formView = {
        name: nameView,
        grid: gridView,
        fields: fieldViews,
    };
    if (isMobile) {
        formView = applyMobileLayout(formView);
    }
    return formView;
}
function applyMobileLayout(form) {
    const { fields } = form.fields.reduce((state, field) => {
        const layout = {
            width: form.grid.columns,
            height: field.layout.height,
            column: 0,
            row: state.row,
        };
        state.fields.push(Object.assign(Object.assign({}, field), { layout }));
        state.row += field.layout.height;
        return state;
    }, { row: 0, fields: [] });
    return Object.assign(Object.assign({}, form), { fields });
}
export function createFieldView(field, layout, path, recursive, styleParams, isMobile) {
    var _a, _b, _c;
    if (!(field.id && ((_a = field.view) === null || _a === void 0 ? void 0 : _a.fieldType))) {
        return;
    }
    const breakpoint = (_b = layout === null || layout === void 0 ? void 0 : layout.large) !== null && _b !== void 0 ? _b : {};
    const items = (_c = breakpoint.items) !== null && _c !== void 0 ? _c : [];
    const item = items.find(({ fieldId }) => fieldId === field.id);
    if (!item) {
        return;
    }
    const layoutView = createLayoutView(item);
    if (!layoutView) {
        return;
    }
    const fieldTarget = field.target || '';
    const fieldPath = path ? `${path}/${fieldTarget}` : fieldTarget;
    const propertiesView = createPropertiesView(field, fieldPath, recursive, styleParams, isMobile);
    return {
        id: field.id,
        fieldType: field.view.fieldType,
        properties: propertiesView,
        layout: layoutView,
        target: fieldTarget,
        path: fieldPath,
    };
}
export function createPropertiesView(field, path, recursive, styleParams, isMobile) {
    var _a, _b, _c;
    const { hidden, pii, nestedFormId } = field;
    const _d = (_a = field.view) !== null && _a !== void 0 ? _a : {}, { label, disabled, defaultValue, fieldType, form } = _d, rest = __rest(_d, ["label", "disabled", "defaultValue", "fieldType", "form"]);
    const { required } = (_b = field.validation) !== null && _b !== void 0 ? _b : {};
    const formView = form && recursive
        ? createFormView({
            form,
            path,
            recursive: false,
            styleParams,
            isMobile,
        })
        : undefined;
    return Object.assign({ label: label !== null && label !== void 0 ? label : undefined, disabled: disabled !== null && disabled !== void 0 ? disabled : false, hidden: hidden !== null && hidden !== void 0 ? hidden : false, required: required !== null && required !== void 0 ? required : false, pii: pii !== null && pii !== void 0 ? pii : false, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : undefined, validation: (_c = field.validation) !== null && _c !== void 0 ? _c : {}, nestedFormId: nestedFormId !== null && nestedFormId !== void 0 ? nestedFormId : undefined, form: formView }, rest);
}
function createLayoutView(layout) {
    const { row, column, width, height } = layout;
    if (row === null ||
        row === undefined ||
        column === null ||
        column === undefined ||
        width === null ||
        width === undefined ||
        height === null ||
        height === undefined) {
        return;
    }
    return { row, column, width, height, static: layout.static };
}
function createGridView(layout, styleParams, isMobile) {
    var _a, _b, _c;
    const { columns, rowHeight, padding, margin } = (_a = layout === null || layout === void 0 ? void 0 : layout.large) !== null && _a !== void 0 ? _a : {};
    let { vertical: marginVertical, horizontal: marginHorizontal } = margin !== null && margin !== void 0 ? margin : {};
    const { vertical: paddingVertical, horizontal: paddingHorizontal } = padding !== null && padding !== void 0 ? padding : {};
    const gridRowHeight = rowHeight !== null && rowHeight !== void 0 ? rowHeight : GRID_DEFAULTS.rowHeight;
    if (styleParams) {
        marginVertical = (_b = styleParams === null || styleParams === void 0 ? void 0 : styleParams.numbers) === null || _b === void 0 ? void 0 : _b.rowSpacing;
        marginHorizontal = (_c = styleParams === null || styleParams === void 0 ? void 0 : styleParams.numbers) === null || _c === void 0 ? void 0 : _c.columnSpacing;
    }
    if (isMobile) {
        marginHorizontal = 0;
    }
    return {
        columns: columns !== null && columns !== void 0 ? columns : GRID_DEFAULTS.columns,
        rowHeight: gridRowHeight,
        margin: {
            vertical: marginVertical !== null && marginVertical !== void 0 ? marginVertical : GRID_DEFAULTS.margin.vertical,
            horizontal: marginHorizontal !== null && marginHorizontal !== void 0 ? marginHorizontal : GRID_DEFAULTS.margin.horizontal,
        },
        padding: {
            vertical: paddingVertical !== null && paddingVertical !== void 0 ? paddingVertical : GRID_DEFAULTS.padding.vertical,
            horizontal: paddingHorizontal !== null && paddingHorizontal !== void 0 ? paddingHorizontal : GRID_DEFAULTS.padding.horizontal,
        },
    };
}
function sortFieldViewsByRow(fieldA, fieldB) {
    return fieldA.layout.row - fieldB.layout.row;
}
function sortFieldViewsByColumn(fieldA, fieldB) {
    return fieldA.layout.column - fieldB.layout.column;
}
//# sourceMappingURL=form-view.js.map